import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { useCreateCustomer, useCreateSupplier } from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { useDataRegion } from "@/app/access";

/**
 * Tạo nhanh khách hàng / nhà cung cấp NGAY TRONG ô chọn.
 *
 * Hệ cũ có lối tắt này, và lý do rất thực tế: nhân viên đang lập một chứng từ
 * thì phát hiện khách chưa có trong danh mục. Không có lối tắt thì họ phải rời
 * biểu mẫu đang dở (mất những gì đã gõ), sang màn Khách hàng, tạo, rồi quay lại
 * làm lại từ đầu.
 *
 * CHỈ hỏi những trường BẮT BUỘC: tên công ty, địa chỉ (với NCC — nó in ra PO),
 * và một người liên hệ. Phần còn lại bổ sung sau ở màn quản lý — hộp thoại này
 * là lối tắt, không phải bản sao của cả biểu mẫu.
 */
export function QuickCreatePartyModal({
  kind,
  onClose,
  onCreated,
}: {
  kind: "customer" | "supplier";
  onClose: () => void;
  /** id vừa tạo — người gọi tự chọn nó vào ô. */
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const createCustomer = useCreateCustomer();
  const createSupplier = useCreateSupplier();
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  const busy = createCustomer.isPending || createSupplier.isPending;
  const region = useDataRegion();
  const isSupplier = kind === "supplier";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const contacts = [{ name: contactName.trim(), phone1: phone.trim() || undefined }];
      const created = isSupplier
        ? await createSupplier.mutateAsync({
            companyName: companyName.trim(),
            address: address.trim(),
            contacts,
          })
        : await createCustomer.mutateAsync({
            companyName: companyName.trim(),
            // Khu vực của người tạo, không thì "vietnam" như biểu mẫu đầy đủ; đổi được
            // sau ở màn Khách hàng. Hộp thoại này không hỏi để giữ đúng vai trò lối tắt.
            classification: region ?? "vietnam",
            contacts,
          });
      onCreated(created.data.id);
      onClose();
    } catch (err) {
      setError(getLocalizedError(err, t, "errors.validation"));
    }
  };

  return (
    <Modal
      eyebrow={t("common.createMode")}
      title={isSupplier ? t("suppliers.newSupplier") : t("customers.newCustomer")}
      closeLabel={t("common.close")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-3 p-4">
        <div>
          <FieldLabel htmlFor="qc-name">{t("customers.company")}</FieldLabel>
          <Input
            id="qc-name"
            required
            minLength={2}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>
        {isSupplier && (
          <div>
            {/* Địa chỉ NCC in ra Đơn đặt hàng nên nó bắt buộc (#143). */}
            <FieldLabel htmlFor="qc-addr">{t("common.address")}</FieldLabel>
            <Input id="qc-addr" required value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="qc-contact">{t("customers.contactName")}</FieldLabel>
            <Input
              id="qc-contact"
              required
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </div>
          <div>
            <FieldLabel htmlFor="qc-phone">{t("common.phone")}</FieldLabel>
            <Input id="qc-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        {error && <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" size="sm" disabled={busy || !companyName.trim() || !contactName.trim()}>
            {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
